/**
 * Mock data for project & space management UI (Z-122).
 *
 * Used by: browse tree, search modal, settings section, showcase page.
 */

// --- Color palette ---

export const PRESET_COLORS = [
  "#30D158",
  "#5E5CE6",
  "#FF6B6B",
  "#FF9F0A",
  "#FFD60A",
  "#64D2FF",
  "#BF5AF2",
  "#FF375F",
] as const;

// --- Search result types ---

export type SearchResultType =
  | "project"
  | "space"
  | "frame"
  | "agent"
  | "memory-node"
  | "memory-edge"
  | "memory-summary";

export interface MockSearchResult {
  id: string;
  type: SearchResultType;
  name: string;
  breadcrumb?: string;
}

export interface MockSearchResultGroup {
  label: string;
  results: MockSearchResult[];
}

// --- Grouped search results for search modal ---

export const MOCK_SEARCH_RESULTS: MockSearchResultGroup[] = [
  {
    label: "Projects",
    results: [
      {
        id: "p1",
        type: "project",
        name: "Marketing Website Redesign",
      },
      {
        id: "p2",
        type: "project",
        name: "Mobile App V2",
      },
    ],
  },
  {
    label: "Spaces",
    results: [
      {
        id: "s1",
        type: "space",
        name: "Research",
        breadcrumb: "Marketing Website Redesign",
      },
      {
        id: "s2",
        type: "space",
        name: "Design",
        breadcrumb: "Marketing Website Redesign",
      },
      {
        id: "s5",
        type: "space",
        name: "Sprint Planning",
        breadcrumb: "Mobile App V2",
      },
    ],
  },
  {
    label: "Frames",
    results: [
      {
        id: "f1",
        type: "frame",
        name: "Agent Chat",
        breadcrumb: "Research · Marketing Website Redesign",
      },
      {
        id: "f2",
        type: "frame",
        name: "api-handler.ts",
        breadcrumb: "Development · Marketing Website Redesign",
      },
      {
        id: "f6",
        type: "frame",
        name: "Onboarding Flow Wireframes",
        breadcrumb: "Design · Marketing Website Redesign",
      },
    ],
  },
  {
    label: "Agents",
    results: [
      {
        id: "a1",
        type: "agent",
        name: "Claude",
        breadcrumb: "Research · Marketing Website Redesign",
      },
      {
        id: "a3",
        type: "agent",
        name: "Code Review Agent",
        breadcrumb: "Development · Marketing Website Redesign",
      },
    ],
  },
  {
    label: "Memory",
    results: [
      {
        id: "mn1",
        type: "memory-node",
        name: "React Flow Architecture",
        breadcrumb: "Marketing Website Redesign",
      },
      {
        id: "mn2",
        type: "memory-node",
        name: "User Research Findings Q1",
        breadcrumb: "Marketing Website Redesign",
      },
      {
        id: "me1",
        type: "memory-edge",
        name: "Architecture → Sprint Planning",
        breadcrumb: "Mobile App V2",
      },
      {
        id: "ms1",
        type: "memory-summary",
        name: "Weekly Summary: Design Decisions",
        breadcrumb: "Marketing Website Redesign",
      },
    ],
  },
];

// --- Recent items for search empty state ---

export interface MockRecentItem {
  id: string;
  type: SearchResultType;
  name: string;
  breadcrumb?: string;
  visitedAt: string;
}

export const MOCK_RECENT_ITEMS: MockRecentItem[] = [
  {
    id: "s2",
    type: "space",
    name: "Design",
    breadcrumb: "Marketing Website Redesign",
    visitedAt: "2026-03-18T11:45:00Z",
  },
  {
    id: "f2",
    type: "frame",
    name: "api-handler.ts",
    breadcrumb: "Development · Marketing Website Redesign",
    visitedAt: "2026-03-18T10:30:00Z",
  },
  {
    id: "mn1",
    type: "memory-node",
    name: "React Flow Architecture",
    breadcrumb: "Marketing Website Redesign",
    visitedAt: "2026-03-17T16:20:00Z",
  },
  {
    id: "p2",
    type: "project",
    name: "Mobile App V2",
    visitedAt: "2026-03-17T14:00:00Z",
  },
  {
    id: "a1",
    type: "agent",
    name: "Claude",
    breadcrumb: "Research · Marketing Website Redesign",
    visitedAt: "2026-03-17T09:15:00Z",
  },
];

// --- Re-exports from mock-data for convenience ---

export {
  MOCK_PROJECTS,
  MOCK_SPACES,
  MOCK_AGENTS,
  MOCK_FRAMES,
  MOCK_USERS,
  MOCK_CURRENT_USER,
} from "@/workshop/data/mock-data";
export type {
  MockProject,
  MockSpace,
  MockAgent,
  MockFrame,
  MockUser,
} from "@/workshop/data/mock-data";
